import * as React from 'react';
import {useContext, useEffect, useRef, useState} from 'react';
import FlexeContext from '../../contexts/FlexeContext';
import {ApiResponse, CompanyType, Warehouse} from '../../shared/CommonInterfaces';
import {ShipmentDocuments} from '../../documents/ShipmentDocuments';
import OutboundFreightLoadService from '../../shared/services/OutboundFreightLoadService';
import OutboundManifestService from '../../shared/services/OutboundManifestService';
import HeaderDetail from '../../shared/HeaderDetail';
import DocumentsService from '../../shared/services/DocumentsService';
import {FreightShipMode, ParcelShipMode} from '../../shared/constants';
import {formatDate} from '../../shared/utilities/DataFormatting';
import LoadsService from '../../shared/services/LoadsService';
import {ManifestStatus, OutboundTrailerManifestDetail} from '../outbound-manifests/ManifestInterfaces';
import {FreightLoad} from '../outbound-freight/FreightInterfaces';
import CancelLoadModal from '../outbound-manifests/CancelLoadModal';
import HeaderEditableDetail, {HeaderEditableDetailType} from '../../shared/HeaderEditableDetail';
import LoadContents from './load-contents/LoadContents';
import {LoadStatusMap} from './LoadConstants';
import {LoadDetailActions} from './LoadDetailActions';
import {UpsertLoadRequest, LoadDetail, UpsertLoadState} from './LoadInterfaces';
import {UpsertLoadModal} from './create-load-modal/UpsertLoadModal';

interface LoadDetailProps {
  loadId: number;
  selectedWarehouse: Warehouse;
  parcelTrailerLoadingEnabled: boolean;
  freightTrailerLoadingEnabled: boolean;
  enableLoadBlockIfNoLabelsModal: boolean;
}

const LoadDetail: React.FC<LoadDetailProps> = (props) => {
  const {authenticityToken} = useContext(FlexeContext);
  const [showEditLoadModal, setShowEditLoadModal] = useState<boolean>(false);
  const resultRef = useRef(null);
  const outboundManifestService = new OutboundManifestService(authenticityToken);
  const freightLoadService = new OutboundFreightLoadService(authenticityToken);
  const documentsService = new DocumentsService(CompanyType.warehouse);
  const [load, setLoad] = useState<LoadDetail>(null);
  const [loadMetadata, setLoadMetadata] = useState<UpsertLoadState>(null);
  // this (hacky) flag allows us to tell LoadContents to re-fetch the shipments when one is added or removed via edit
  // its value is meaningless... LoadContents will only care when it changes
  const [refreshShipmentsTrigger, setRefreshShipmentsTrigger] = useState<boolean>(false);

  // setShipmentIds is needed to get the shipment IDs from the LoadContents (where they are fetched).
  // shipmentIds are needed for the edit flow to work at the moment, as the API will attempt to remove
  // any shipmentIds that were not passed to it but were found attached to the load.
  const [shipmentIds, setShipmentIds] = useState<number[] | null>(null);

  const [billOfLadingIsGenerating, setBillOfLadingIsGenerating] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);
  const loadsService = new LoadsService(authenticityToken);
  const [error, setError] = useState<string | null>(null);
  const [showCancelLoadActionModal, setShowCancelLoadActionModal] = useState<boolean>(false);

  const breadcrumbs = (
    <div className="breadcrumbs" ref={resultRef}>
      <a href="/wh/loads">Loads</a>
      <span className="navigation-separator">/</span>
      {props.loadId}
    </div>
  );

  const toggleEditing = () => {
    setShowEditLoadModal(!showEditLoadModal && load?.shipMode === FreightShipMode);
    setEditing(!editing);
  };

  const enqueueBillOfLadingGeneration = async () => {
    setError(null);
    try {
      setBillOfLadingIsGenerating(true);
      const response = await freightLoadService.enqueueBillOfLadingGeneration(load.freightLoadId.toString());
      if (response.errors?.length > 0) {
        showApiResponseError(response);
      }
    } catch (e) {
      setError(e.toString());
    }
  };

  async function getManifestDetails(loadId: number): Promise<OutboundTrailerManifestDetail> {
    setError(null);
    try {
      const getManifestDetailResponse = await outboundManifestService.getManifestDetail(loadId);
      if (getManifestDetailResponse.errors?.length > 0) {
        showApiResponseError(getManifestDetailResponse);
      }
      return getManifestDetailResponse.data;
    } catch (e) {
      setError(e.toString());
    }
  }

  async function getFreightLoadDetails(freightLoadId: number): Promise<FreightLoad> {
    setError(null);
    try {
      const getFreightLoadsResponse = await freightLoadService.getFreightLoads([freightLoadId]);
      if (getFreightLoadsResponse.errors?.length > 0) {
        showApiResponseError(getFreightLoadsResponse);
      }
      return getFreightLoadsResponse.data.freightLoads[0];
    } catch (e) {
      setError(e.toString());
    }
  }

  const fetchLoadDetails = async () => {
    setError(null);
    const manifestDetails = await getManifestDetails(props.loadId);
    let loadDetails: LoadDetail;

    switch (manifestDetails.shipMode) {
      case FreightShipMode: {
        const freightLoad = await getFreightLoadDetails(manifestDetails.freightLoadId);

        // assumption: loads only have one stop
        const freightLoadStop = freightLoad.loadStops[0];

        loadDetails = {
          id: manifestDetails.id,
          status: manifestDetails.status,
          shipMode: FreightShipMode,
          destinationId: freightLoadStop.destinationTag,
          createdAt: manifestDetails.createdAt,
          updatedAt: manifestDetails.updatedAt,
          scac: freightLoad.scac,
          freightLoadGroup: freightLoad.freightLoadGroup,
          carrier: null,
          proNumber: freightLoadStop.proNumber,
          trailerNumber: freightLoad.trailerNumber,
          sealNumber: freightLoadStop.sealNumber,
          dockLocationLabel: manifestDetails.doorLocation,
          dockLocationId: manifestDetails.dockLocationId,
          totalWeight: manifestDetails.totalWeight.toString(10),
          appointmentTime: freightLoad.scheduledShipDate,
          loadStopId: freightLoadStop.id,
          bolNumber: freightLoadStop.bolNumber,
          freightLoadId: freightLoad.id,
          reservationId: manifestDetails.reservationId
        };
        break;
      }
      case ParcelShipMode: {
        loadDetails = {
          id: manifestDetails.id,
          status: manifestDetails.status,
          shipMode: ParcelShipMode,
          destinationId: null,
          createdAt: manifestDetails.createdAt,
          updatedAt: manifestDetails.updatedAt,
          scac: null,
          freightLoadGroup: null,
          carrier: manifestDetails.scacCode,
          proNumber: null,
          trailerNumber: manifestDetails.trailerId,
          sealNumber: null,
          dockLocationLabel: manifestDetails.doorLocation,
          dockLocationId: manifestDetails.dockLocationId,
          totalWeight: manifestDetails.totalWeight.toString(10),
          appointmentTime: null,
          loadStopId: null,
          bolNumber: null,
          freightLoadId: null,
          reservationId: manifestDetails.reservationId
        };
        break;
      }
    }

    const editableLoadFields: UpsertLoadState = {
      loadId: loadDetails.id,
      freightLoadId: loadDetails.freightLoadId,
      createdAt: loadDetails.createdAt,
      carrier: loadDetails.carrier,
      scac: loadDetails.scac,
      trailerNumber: loadDetails.trailerNumber,
      proNumber: loadDetails.proNumber,
      sealNumber: loadDetails.sealNumber,
      dockLocationId: loadDetails.dockLocationId,
      dockLocationLabel: loadDetails.dockLocationLabel,
      destinationTag: loadDetails.destinationId,
      shipmentIds
    };

    setLoad(loadDetails);
    setLoadMetadata(editableLoadFields);
  };

  useEffect(() => {
    fetchLoadDetails();
  }, [authenticityToken]);

  useEffect(() => {
    setLoadMetadata({
      ...loadMetadata,
      shipmentIds
    });
  }, [shipmentIds]);

  const showApiResponseError = (response: ApiResponse<any>) => {
    if (response.errors?.length <= 0) {
      return;
    }

    // Grab the first error because there shouldn't ever be more than one
    const e = response.errors[0];
    setError(`${e.title}, ${e.detail}`);
  };

  const placeholderStub = () => {
    return;
  };

  const onStartEdits = () => {
    setError(null);
    toggleEditing();
  };

  const onCancelEdits = () => {
    toggleEditing();
  };

  const parcelLoadEditsWereMade = (): boolean => {
    const trailerNumberChanged = loadMetadata?.trailerNumber !== load?.trailerNumber;
    const carrierChanged = loadMetadata?.carrier !== load?.carrier;
    return [trailerNumberChanged, carrierChanged].some((change) => change);
  };

  // trailer number was changed in the parcel load edit flow
  const onParcelTrailerNumberChange = (newTrailerNumber: string) => {
    setLoadMetadata({
      ...loadMetadata,
      trailerNumber: newTrailerNumber
    });
  };

  const onCarrierChange = (newCarrier: string) => {
    setLoadMetadata({
      ...loadMetadata,
      carrier: newCarrier
    });
  };

  /*
    Freight loads are edited and committed through the UpsertLoadModal. All we need to do is
    update the state when an edit is made so that the metadata displayed in the header is up-to-date.
   */
  const onSaveFreightLoadEdits = (loadWithEdits: UpsertLoadState) => {
    if (shipmentWasAddedOrRemoved(loadWithEdits.shipmentIds)) {
      setRefreshShipmentsTrigger(!refreshShipmentsTrigger);
    }
    setLoadMetadata(loadWithEdits);
    toggleEditing();
  };

  const shipmentWasAddedOrRemoved = (newShipmentIds: number[]): boolean => {
    if (shipmentIds.length !== newShipmentIds.length) {
      return true;
    }
    const oldSorted = shipmentIds.sort((n1, n2) => n1 - n2);
    const newSorted = newShipmentIds.sort((n1, n2) => n1 - n2);
    return oldSorted.some((id, index) => id !== newSorted[index]);
  };

  // Parcel loads are edited and committed in this component using in-line edits
  const onSaveParcelLoadEdits = async () => {
    setError(null);
    if (!parcelLoadEditsWereMade()) {
      setEditing(false);
      return;
    }
    try {
      const data: UpsertLoadRequest = {
        loadId: load.id,
        warehouseId: props.selectedWarehouse.id,
        scac: load.scac,
        trailerNumber: loadMetadata?.trailerNumber,
        proNumber: load.proNumber,
        sealNumber: load.sealNumber,
        carrier: loadMetadata?.carrier,
        shipmentIds,
        dockLocationId: load.dockLocationId
      };
      const response = await loadsService.upsertLoad(data);
      if (response.errors?.length > 0) {
        showApiResponseError(response);
        onCancelEdits();
      }

      setLoad((prevLoad) => ({
        ...prevLoad,
        trailerNumber: loadMetadata?.trailerNumber,
        carrier: loadMetadata?.carrier
      }));
    } catch (e) {
      setError(e.toString());
      onCancelEdits();
    } finally {
      toggleEditing();
    }
  };

  const onShip = async () => {
    setError(null);
    try {
      const response = await outboundManifestService.completeAndSubmitToCarrier(load.id);
      if (response.errors?.length > 0) {
        showApiResponseError(response);
      } else {
        setLoad((prevLoad) => ({
          ...prevLoad,
          status: ManifestStatus.completed
        }));
      }
    } catch (e) {
      setError(e.toString());
    }
  };

  const onCancelLoad = async () => {
    setError(null);
    try {
      const response = await outboundManifestService.cancelManifest(load.id);
      if (response.errors?.length > 0) {
        showApiResponseError(response);
      } else {
        setLoad((prevLoad) => ({
          ...prevLoad,
          status: ManifestStatus.cancelled
        }));
      }
    } catch (e) {
      setError(e.toString());
    }
    toggleCancelLoadModal();
  };

  const toggleCancelLoadModal = () => setShowCancelLoadActionModal(!showCancelLoadActionModal);

  return (
    <div className="load-detail-page container-fluid">
      {showCancelLoadActionModal && (
        <CancelLoadModal
          onCancel={onCancelLoad}
          loadId={load?.id.toString()}
          isOpen={showCancelLoadActionModal}
          toggleModal={toggleCancelLoadModal}
        />
      )}
      {breadcrumbs}
      <div className="load-metadata-button-wrapper">
        <LoadDetailActions
          onStartEdits={onStartEdits}
          onCancelEdits={onCancelEdits}
          onSaveEdits={onSaveParcelLoadEdits}
          onShip={onShip}
          onCancelLoad={toggleCancelLoadModal}
          shipMode={load?.shipMode}
          editing={editing}
          loadStatus={load?.status}
        />
      </div>
      <h1>Load #{props.loadId}</h1>
      <span className={`load-status ${LoadStatusMap.get(load?.status)?.className}`}>
        {LoadStatusMap.get(load?.status)?.text}
      </span>
      <br />
      <div>
        {error != null && (
          <div className="alert alert-danger" role="alert" key={`error:${1}`}>
            {error}
          </div>
        )}
      </div>
      <table style={{width: '100%'}}>
        <tbody>
          <tr>
            <th>
              <div id="metadata">
                <div className="col-md-4">
                  <HeaderDetail
                    label="Mode:"
                    classes={['non-bold']}
                    value={load?.shipMode.charAt(0).toUpperCase() + load?.shipMode.slice(1) || '--'}
                  />
                  {load?.freightLoadGroup ? (
                    <HeaderDetail label="Load Group:" classes={['non-bold']} value={load?.freightLoadGroup || '--'} />
                  ) : (
                    <HeaderDetail
                      label="Destination ID:"
                      classes={['non-bold']}
                      value={loadMetadata?.destinationTag || '--'}
                    />
                  )}
                  <HeaderDetail
                    label="Created At:"
                    classes={['non-bold']}
                    value={load?.createdAt ? formatDate(new Date(load.createdAt), ' MMM D, h:mm a') : '--'}
                  />
                  <HeaderDetail
                    label="Last Update:"
                    classes={['non-bold']}
                    value={load?.updatedAt ? formatDate(new Date(load?.updatedAt), ' MMM D, h:mm a') : '--'}
                  />
                  <div className="vertical-rule" />
                </div>
                <div className="col-md-2">
                  {load?.shipMode === FreightShipMode && (
                    <HeaderDetail
                      label={'SCAC:'}
                      classes={['non-bold']}
                      value={loadMetadata?.scac}
                      hideWhenEmpty={false}
                    />
                  )}
                  {load?.shipMode === ParcelShipMode && (
                    <HeaderEditableDetail
                      label={'Carrier:'}
                      value={loadMetadata?.carrier}
                      isEditing={editing}
                      type={HeaderEditableDetailType.text}
                      onChange={onCarrierChange}
                      classes={['carrier-input', 'non-bold']}
                      maxLength={50}
                    />
                  )}
                  {load?.shipMode === FreightShipMode && (
                    <HeaderDetail
                      label={'PRO #:'}
                      value={loadMetadata?.proNumber}
                      classes={['pro-input', 'non-bold']}
                      hideWhenEmpty={false}
                    />
                  )}
                  <HeaderEditableDetail
                    label={'Trailer #:'}
                    value={loadMetadata?.trailerNumber}
                    isEditing={editing && load?.shipMode === ParcelShipMode} // freight edits this via modal
                    type={HeaderEditableDetailType.text}
                    onChange={onParcelTrailerNumberChange}
                    maxLength={50}
                    classes={['trailer-input', 'non-bold']}
                  />
                  {load?.shipMode === FreightShipMode && (
                    <HeaderDetail
                      label={'Seal #:'}
                      value={loadMetadata?.sealNumber}
                      classes={['trailer-input', 'non-bold']}
                      hideWhenEmpty={false}
                    />
                  )}
                  <div className="vertical-rule" />
                </div>
                <div className="col-md-3">
                  <HeaderDetail
                    label={'Dock Loc:'}
                    classes={['non-bold']}
                    value={
                      <a href={`/wh/locations/${loadMetadata?.dockLocationId}`}>{loadMetadata?.dockLocationLabel}</a>
                    }
                    hideWhenEmpty={false}
                  />
                  <HeaderDetail
                    label="Total Weight:"
                    classes={['non-bold']}
                    value={(load?.totalWeight || '--') + ' lb'}
                    hideWhenEmpty={false}
                  />
                  <HeaderDetail label="Appt. Time:" classes={['non-bold']} value={load?.appointmentTime} />
                </div>
              </div>
            </th>
          </tr>
          {load?.shipMode === FreightShipMode && (
            <tr>
              <td>
                <div className="container-fluid">
                  <ShipmentDocuments
                    authenticityToken={authenticityToken}
                    reservationId={load.reservationId}
                    documentsService={documentsService}
                    stopId={load.loadStopId}
                    bolNumber={load.bolNumber}
                    billOfLadingIsGenerating={billOfLadingIsGenerating}
                    enqueueBillOfLadingGeneration={enqueueBillOfLadingGeneration}
                    batchId={0} // this needs to be made real or removed
                    displayPrintLpnLabelsButton={false} // this needs to be made real or removed
                    enableFreightPackingListRefresh={false} // this needs to be made real or removed
                    freightPackingListIsGenerating={false} // this needs to be made real or removed
                    shipmentStatus={''} // this needs to be made real or removed
                    showUploadButton={false} // this needs to be made real or removed
                    ssccLabelsAreGenerating={false} // this needs to be made real or removed
                    checkLabelRequirementsForCompletion={placeholderStub} // this needs to be made real or removed
                    enqueueSsccLabelRegeneration={placeholderStub} // this needs to be made real or removed
                    enqueueFreightPackingListRegeneration={placeholderStub} // this needs to be made real or removed
                  />
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <LoadContents
        authenticityToken={authenticityToken}
        warehouse={props.selectedWarehouse}
        load={load}
        refreshShipmentsTrigger={refreshShipmentsTrigger}
        onShipmentIdsFetched={setShipmentIds}
      />
      <UpsertLoadModal
        isOpen={showEditLoadModal}
        toggleModal={toggleEditing}
        cancelUpsert={toggleEditing}
        warehouseId={props.selectedWarehouse.id}
        loadState={loadMetadata}
        onEditLoad={onSaveFreightLoadEdits}
      />
    </div>
  );
};

export default LoadDetail;
